import { graphql } from '~/lib/datocms/graphql'

/**
 * Let's define the GraphQL fragment needed for the component to function.
 *
 * GraphQL fragment colocation keeps queries near the components using them,
 * improving maintainability and encapsulation. Fragment composition enables
 * building complex queries from reusable parts, promoting code reuse and
 * efficiency. Together, these practices lead to more modular, maintainable, and
 * performant GraphQL implementations by allowing precise data fetching and
 * easier code management.
 *
 * Learn more: https://gql-tada.0no.co/guides/fragment-colocation
 */

export const TagFragment = graphql(`
  fragment TagFragment on Tag @_unmask {
    tag
    attributes
    content
  }
`)

export const ResponsiveImage = graphql(/* GraphQL */ `
  fragment ResponsiveImage on ResponsiveImage {
    srcSet
    webpSrcSet
    sizes
    src
    width
    height
    aspectRatio
    alt
    title
    bgColor
    base64
  }
`)

export const Testimonial = graphql(
  /* GraphQL */ `
    fragment Testimonial on TestimonialRecord {
      media {
        title
        customData
        url
        width
        height
        alt
        mimeType
        title
        format
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fit: crop, ar: "1:1", w: 80 }
        ) {
          ...ResponsiveImage
        }
      }
      name
      title
      company
      quote
    }
  `,
  [ResponsiveImage]
)

export const Button = graphql(/* GraphQL */ `
  fragment Button on ButtonRecord {
    label
    variant
    reference {
      __typename
      ... on ArticleRecord {
        _modelApiKey
        slug
      }
      ... on PageRecord {
        _modelApiKey
        slug
      }
      ... on IndexRecord {
        _modelApiKey
      }
    }
    externalLink
  }
`)

export const Client = graphql(
  /* GraphQL */ `
    fragment Client on ClientRecord {
      name
      logo {
        alt
        url
        width
        height
        url
      }
      textureDark {
        alt
        url
        width
        height
        url
        responsiveImage {
          ...ResponsiveImage
        }
      }
      textureLight {
        alt
        url
        width
        height
        url
        responsiveImage {
          ...ResponsiveImage
        }
      }
      slug
    }
  `,
  [ResponsiveImage]
)

export const CallToAction = graphql(/* GraphQL */ `
  fragment CallToAction on CtaRecord {
    externalLink
    pageLink {
      __typename
      ... on ArticleRecord {
        _modelApiKey
        slug
      }
      ... on PageRecord {
        _modelApiKey
        slug
      }
    }
    primary
    title
  }
`)

export const Collection = graphql(
  /* GraphQL */ `
    fragment Collection on CollectionBlockRecord {
      records {
        ... on RecordInterface {
          id
          _modelApiKey
          __typename
        }
        ... on ArticleRecord {
          _modelApiKey
          slug
        }
        ... on TestimonialRecord {
          _modelApiKey
          ...Testimonial
        }
      }
    }
  `,
  [Testimonial]
)

export const Fact = graphql(/* GraphQL */ `
  fragment Fact on FactRecord {
    value
    text
  }
`)

export const Glb = graphql(/* GraphQL */ `
  fragment Glb on GlbRecord {
    title
    model {
      url
    }
  }
`)

export const Facts = graphql(
  /* GraphQL */ `
    fragment Facts on FactListRecord {
      facts {
        ...Fact
      }
    }
  `,
  [Fact]
)

export const Faq = graphql(/* GraphQL */ `
  fragment Faq on FaqRecord {
    question
    answer {
      links {
        __typename
        ... on PageRecord {
          _modelApiKey
          id
          title
          slug
        }
        ... on ArticleRecord {
          _modelApiKey
          id
          slug
          title
        }
      }
      value
    }
  }
`)

export const Features = graphql(/* GraphQL */ `
  fragment Features on FeatureListRecord {
    blocks {
      ... on FeatureRecord {
        title
        body
      }
    }
  }
`)

export const Iframe = graphql(/* GraphQL */ `
  fragment Iframe on IframeRecord {
    title
    src
    width
    height
  }
`)

export const Job = graphql(/* GraphQL */ `
  fragment Job on JobRecord {
    __typename
    _modelApiKey
    slug
    title
    time
    location
  }
`)

export const Media = graphql(
  /* GraphQL */ `
    fragment Media on MediaBlockRecord {
      mediaBlockAssets {
        __typename
        title
        customData
        url
        width
        height
        alt
        mimeType
        title
        format
        responsiveImage(imgixParams: { auto: compress, crop: focalpoint, fit: crop }) {
          ...ResponsiveImage
        }
        video {
          duration
          framerate
          mp4Url(res: medium, exactRes: medium)
          thumbnailUrl
          streamingUrl
          muxPlaybackId
          muxAssetId
        }
      }
      layout
    }
  `,
  [ResponsiveImage]
)

export const Press = graphql(
  /* GraphQL */ `
    fragment Press on PressRecord {
      media {
        alt
        url
        width
        height
        responsiveImage(imgixParams: { auto: compress, crop: focalpoint, fit: crop }) {
          ...ResponsiveImage
        }
      }
      published
      title
      excerpt
      url
    }
  `,
  [ResponsiveImage]
)

export const Presses = graphql(
  /* GraphQL */ `
    fragment Presses on PressListRecord {
      blocks {
        ...Press
      }
    }
  `,
  [Press]
)

export const PullQuote = graphql(
  /* GraphQL */ `
    fragment PullQuote on PullQuoteRecord {
      quote
      media {
        alt
        url
        width
        height
        responsiveImage(imgixParams: { auto: compress, crop: focalpoint, fit: crop }) {
          ...ResponsiveImage
        }
      }
      name
      title
      company
    }
  `,
  [ResponsiveImage]
)

export const Table = graphql(/* GraphQL */ `
  fragment Table on TableRecord {
    table
    caption
  }
`)

export const Teaser = graphql(
  /* GraphQL */ `
    fragment Teaser on TeaserRecord {
      label
      title
      text
      media {
        alt
        url
        width
        height
        responsiveImage(
          imgixParams: { auto: compress, crop: focalpoint, fit: fillmax, w: 670, ar: "3:2" }
        ) {
          ...ResponsiveImage
        }
      }
      callToAction {
        ...CallToAction
      }
    }
  `,
  [ResponsiveImage, CallToAction]
)

export const TeaserBlock = graphql(/* GraphQL */ `
  fragment TeaserBlock on TeaserBlockRecord {
    label
    text
  }
`)

export const Video = graphql(/* GraphQL */ `
  fragment Video on VideoRecord {
    videoLoop {
      height
      width
      video {
        duration
        framerate
        mp4Url(res: medium, exactRes: medium)
        thumbnailUrl
        streamingUrl
        muxPlaybackId
        muxAssetId
      }
    }
    videoFull {
      height
      width
      video {
        duration
        framerate
        mp4Url(res: medium, exactRes: medium)
        thumbnailUrl
        streamingUrl
        muxPlaybackId
        muxAssetId
      }
    }
  }
`)

export const VideoExternal = graphql(/* GraphQL */ `
  fragment VideoExternal on VideoExternalRecord {
    video {
      url
      title
      thumbnailUrl
      height
      provider
      providerUid
      width
    }
  }
`)
